import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  collections_import_route,
  collections_route,
  platforms_shopify_metafield_definitions,
} from "@/constants";
import { authedFetch, isTokenInvalid } from "@/lib/api-client";
import { collectionBody } from "@/lib/collections";
import type {
  Collection,
  CollectionImportResponse,
  CollectionTitle,
  CollectionInput,
  CollectionProductsResponse,
  CollectionSyncResponse,
  ProductMetafieldDefinition,
} from "@/lib/types";
import { getOrgIdWithFallback } from "@/lib/utils";
import { queryClient } from "@/main";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import i18n from "@/i18n";
import type { AuthFetchParams } from "./global";

// the API explains invalid collections in msg, e.g. a rule without a condition
export class CollectionError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function readError(res: Response) {
  const data = (await res.json().catch(() => null)) as {
    msg?: unknown;
  } | null;
  const message =
    res.status < 500 && typeof data?.msg === "string"
      ? data.msg
      : i18n.t("collections.toast.failed");
  return new CollectionError(res.status, message);
}

async function getJSON<T>(path: string, params: AuthFetchParams) {
  const res = await authedFetch(path, {
    method: "GET",
    token: params.token,
    orgId: params.orgId,
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  if (!res.ok) throw await readError(res);

  return (await res.json()) as T;
}

export const collectionsQueryOpts = (params: AuthFetchParams, search = "") =>
  queryOptions({
    queryKey: ["collections", "list", search.trim()],
    queryFn: async () => {
      const query = new URLSearchParams();
      if (search.trim()) query.set("search", search.trim());
      return await getJSON<Collection[]>(
        `${collections_route}?${query}`,
        params,
      );
    },
  });

// the collections products can be filtered by, without their products counts,
// which take the API a while for smart collections
export const collectionTitlesQueryOpts = (params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["collections", "list", { counts: false }],
    queryFn: async () =>
      await getJSON<CollectionTitle[]>(
        `${collections_route}?counts=false`,
        params,
      ),
  });

export const useCollectionTitles = () => {
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  return useQuery(
    collectionTitlesQueryOpts({
      token: getToken() as string,
      setToken,
      orgId: getOrgIdWithFallback(orgId),
    }),
  );
};

export const collectionQueryOpts = (id: string, params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["collections", id],
    queryFn: async () =>
      await getJSON<Collection>(`${collections_route}/${id}`, params),
    // a missing collection won't appear by trying again
    retry: (count, error) =>
      !(error instanceof CollectionError && error.status === 404) && count < 3,
  });

/**
 * The product metafield definitions of the organization's Shopify stores that
 * the aspects of a collection's eBay categories can take their values from.
 */
export const productMetafieldDefinitionsQueryOpts = (params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["shopifyMetafieldDefinitions"],
    queryFn: async () =>
      (
        await getJSON<{ definitions: ProductMetafieldDefinition[] }>(
          platforms_shopify_metafield_definitions,
          params,
        )
      ).definitions,
    // they're added in Shopify now and then
    staleTime: 5 * 60 * 1000,
  });

export const collectionProductsQueryOpts = (
  id: string,
  params: AuthFetchParams,
  page = 1,
  pageSize = 30,
) =>
  queryOptions({
    queryKey: ["collections", id, "products", page, pageSize],
    queryFn: async () =>
      await getJSON<CollectionProductsResponse>(
        `${collections_route}/${id}/products?page=${page}&pageSize=${pageSize}`,
        params,
      ),
  });

/**
 * Sends a collection request as the current user, errors carry the API's message.
 */
function useCollectionFetch() {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();

  return async (
    path: string,
    method: "POST" | "DELETE",
    body?: unknown,
  ): Promise<Response | undefined> => {
    const res = await authedFetch(path, {
      method,
      token: getToken() as string,
      orgId: orgId as string,
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });

    if (await isTokenInvalid(res)) {
      await handleTokenInvalid();
      return;
    }

    const token = res.headers.get("access-token");
    if (token) setToken(token);

    if (!res.ok) throw await readError(res);

    return res;
  };
}

function showError(error: Error) {
  toast.error(
    error instanceof CollectionError
      ? error.message
      : i18n.t("collections.toast.failed"),
  );
}

// collection pages read the collection, its products and the list
function invalidateCollection(id?: string) {
  return queryClient.invalidateQueries({
    queryKey: id ? ["collections", id] : ["collections"],
  });
}

export const useCreateCollection = () => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async (input: CollectionInput) => {
      const res = await send(collections_route, "POST", collectionBody(input));
      return res && ((await res.json()) as Collection);
    },
    onSuccess: () => {
      toast.success(i18n.t("collections.toast.created"));
      queryClient.invalidateQueries({ queryKey: ["collections", "list"] });
    },
    onError: showError,
  });
};

export const useUpdateCollection = (id: string) => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async (input: CollectionInput) => {
      const res = await send(
        `${collections_route}/${id}`,
        "POST",
        collectionBody(input),
      );
      return res && ((await res.json()) as Collection);
    },
    onSuccess: (collection) => {
      toast.success(i18n.t("collections.toast.updated"));
      if (collection) {
        queryClient.setQueryData(["collections", id], collection);
      }
      // new rules or sort order change the products
      invalidateCollection(id);
      queryClient.invalidateQueries({ queryKey: ["collections", "list"] });
    },
    onError: showError,
  });
};

export const useDeleteCollection = () => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async (id: string) => {
      await send(`${collections_route}/${id}`, "DELETE");
    },
    onSuccess: (_data, id) => {
      toast.success(i18n.t("collections.toast.deleted"));
      queryClient.removeQueries({ queryKey: ["collections", id] });
      queryClient.invalidateQueries({ queryKey: ["collections", "list"] });
    },
    onError: showError,
  });
};

export const useAddCollectionProducts = (id: string) => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      await send(`${collections_route}/${id}/products`, "POST", { ids });
    },
    onSuccess: (_data, ids) => {
      toast.success(
        i18n.t("collections.toast.productsAdded", { count: ids.length }),
      );
    },
    onError: showError,
    onSettled: () => {
      invalidateCollection(id);
      queryClient.invalidateQueries({ queryKey: ["collections", "list"] });
    },
  });
};

export const useRemoveCollectionProducts = (id: string) => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      await send(`${collections_route}/${id}/products`, "DELETE", { ids });
    },
    onSuccess: (_data, ids) => {
      toast.success(
        i18n.t("collections.toast.productsRemoved", { count: ids.length }),
      );
    },
    onError: showError,
    onSettled: () => {
      invalidateCollection(id);
      queryClient.invalidateQueries({ queryKey: ["collections", "list"] });
    },
  });
};

// moves a product to a zero based position in the whole collection
export const useReorderCollectionProducts = (id: string) => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async (move: { product_id: string; new_position: number }) => {
      await send(`${collections_route}/${id}/products/reorder`, "POST", {
        moves: [move],
      });
    },
    onError: showError,
    onSettled: () => invalidateCollection(id),
  });
};

// imports every collection of a Shopify store, ones imported before are updated
export const useImportShopifyCollections = () => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async (platformConnectionId: string) => {
      const res = await send(collections_import_route, "POST", {
        platform_connection_id: platformConnectionId,
      });
      return res && ((await res.json()) as CollectionImportResponse);
    },
    onSuccess: (response) => {
      if (!response) return;
      if (response.status === "in_progress") {
        toast.info(i18n.t("collections.import.inProgress"));
        return;
      }

      const { created, updated, skipped_products } = response.result;
      toast.success(i18n.t("collections.import.done", { created, updated }), {
        description:
          skipped_products > 0
            ? i18n.t("collections.import.skipped", { count: skipped_products })
            : undefined,
      });
    },
    onError: showError,
    onSettled: () => invalidateCollection(),
  });
};

// replaces an imported collection's details and products with its store's
export const useSyncCollection = (id: string) => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async () => {
      const res = await send(`${collections_route}/${id}/sync`, "POST");
      return res && ((await res.json()) as CollectionSyncResponse);
    },
    onSuccess: (response) => {
      if (!response) return;
      if (response.status === "in_progress") {
        toast.info(i18n.t("collections.sync.inProgress"));
        return;
      }

      toast.success(i18n.t("collections.sync.done"));
      queryClient.setQueryData(["collections", id], response.collection);
    },
    onError: showError,
    onSettled: () => {
      invalidateCollection(id);
      queryClient.invalidateQueries({ queryKey: ["collections", "list"] });
    },
  });
};

// deletes the collections in ids, their products stay
export const useDeleteCollections = () => {
  const send = useCollectionFetch();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await send(collections_route, "DELETE", { ids });
      return res && ((await res.json()) as { deleted: number });
    },
    onSuccess: (response, ids) => {
      toast.success(
        i18n.t("collections.toast.deletedMany", {
          count: response?.deleted ?? ids.length,
        }),
      );
      for (const id of ids) {
        queryClient.removeQueries({ queryKey: ["collections", id] });
      }
    },
    onError: showError,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["collections", "list"] }),
  });
};
